import { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { useEditRecruitmentContext } from '../../Edit/Contexts/EditRecruitmentContext';
import StepProps from '../StepProps';
import ExperienceStepRecalculationDialog from './Components/ExperienceStepRecalculationDialog';
import IdentifyExperienceStep from './IdentifyExperienceStep';
import type { IdentifyExperienceFormValues } from './IdentifyExperienceStep.schema';
import { mapRhfCriteriaToExperienceCriteria } from './IdentifyExperienceStepUtils';
import { useNotificationContext } from '@/Context/NotificationContext';
import useCompanyId from '@/Hooks/UseCompanyId';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';

const EditIdentifyExperienceStep = ({ activeStep, handleBack }: StepProps) => {
    const navigate = useNavigate();
    const companyId = useCompanyId();

    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const [triggerBlocker, setTriggerBlocker] = useState<boolean>(false);

    const {
        processingState,
        experienceCriteriaProcessingState,
        experienceCriteria,
        setExperienceCriteria: setExperienceCriteriaForm,
        resetExperienceCriteriaProcessingState,
        client,
        updateRecruitmentWithExperienceCriteriaAsync,
        isExperienceScoreRecalculationComplete,
        pollExperienceCriteriaScoreRecalculationStateAsync,
    } = useEditRecruitmentContext();

    const navigateAwayFromPage = () => {
        showSuccessToaster(UIStrings.RecruitmentUpdated);

        // navigate to client recruitment overview.
        const createRecruitmentPath = generatePath(RoutePath.AssessmentsActive, {
            companyId,
            clientId: client.id,
        });
        navigate(createRecruitmentPath);
    };

    const onSubmit = async ({ criteria }: IdentifyExperienceFormValues) => {
        try {
            await updateRecruitmentWithExperienceCriteriaAsync(criteria.map(mapRhfCriteriaToExperienceCriteria));
            // if the update recruitment first polling of recalculation state gives "incomplete/executing" state, block and poll
            setTriggerBlocker(true);
        } catch {
            showFailToaster(UIStrings.WeAreStillWorkingOnYourPreviousRecalculationRequest);
        }
    };

    useEffect(() => {
        const pollingIntervalId = setInterval(async () => {
            if (triggerBlocker) {
                if (isExperienceScoreRecalculationComplete) {
                    setTriggerBlocker(false);
                    navigateAwayFromPage();
                } else {
                    await pollExperienceCriteriaScoreRecalculationStateAsync();
                }
            }
        }, 1000); // Poll the backend every 1000ms

        return () => {
            clearInterval(pollingIntervalId); // Clean up on unmount
        };
    }, [triggerBlocker, isExperienceScoreRecalculationComplete]);

    return (
        <>
            <IdentifyExperienceStep
                activeStep={activeStep}
                handleBack={handleBack}
                processingState={processingState}
                experienceCriteriaProcessingState={experienceCriteriaProcessingState}
                experienceCriteria={experienceCriteria}
                setExperienceCriteria={setExperienceCriteriaForm}
                resetExperienceCriteriaProcessingState={resetExperienceCriteriaProcessingState}
                onSubmit={onSubmit}
                isEditMode
            />
            <ExperienceStepRecalculationDialog open={triggerBlocker && !isExperienceScoreRecalculationComplete} />
        </>
    );
};

export default EditIdentifyExperienceStep;
